/**
 * Visual CPU-affinity picker. Renders a grid of one square per logical core
 * (read from machine telemetry), and translates the selection to/from the
 * bitmask string stored on Server_AFFINITY.
 *
 * Input format detection (so we can read both v1-era decimal values and any
 * hex format saved by the new UI):
 *   - empty / null         → system default (no cores selected)
 *   - "0xFE" / "0xfe"      → hex bitmask
 *   - "65534"              → decimal (v1 convention — Server_AFFINITY hint
 *                            in the legacy NewServer wizard was "e.g. 65534")
 *   - "FE" / "ff00"        → hex (treated as hex when the string contains
 *                            a-f letters that disambiguate it from decimal)
 *   - all-digit ambiguous  → decimal (preserves v1 convention)
 *
 * Output format: "0x"-prefixed uppercase hex (e.g. "0xFFFE"). The Node agent
 * applies it via `$p.ProcessorAffinity = ${mask}` in PowerShell, which
 * understands both decimal and "0x"-prefixed literals — but NOT bare-hex
 * "FE", which would parse as a variable reference.
 */
import { useMemo } from 'react';

interface Props {
  /** Total logical cores on the target machine (from telemetry). 0/undef → manual hex. */
  cores: number | undefined;
  /** Current affinity, as an uppercase hex string. '' or null = system default. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

export default function CorePicker({ cores, value, onChange, disabled }: Props) {
  const selected = useMemo(() => parseAffinity(value, cores ?? 64), [value, cores]);

  if (!cores || cores <= 0) {
    return (
      <div className="space-y-1.5">
        <input
          className="input w-48 font-mono"
          placeholder="(default)"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9a-fA-FxX]/g, ''))}
          maxLength={18}
        />
        <div className="text-xs text-fg-dim">
          Bitmask — decimal (e.g. <code>65534</code>) or hex with prefix (e.g. <code>0xFFFE</code>).
          The picker grid appears here once the agent reports its core count.
        </div>
      </div>
    );
  }

  const toggle = (idx: number) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    onChange(formatAffinity(next, cores));
  };

  const setAll = (val: boolean) => {
    if (disabled) return;
    const next = new Set<number>();
    if (val) for (let i = 0; i < cores; i++) next.add(i);
    onChange(formatAffinity(next, cores));
  };

  const setRange = (start: number, count: number) => {
    if (disabled) return;
    const next = new Set<number>();
    for (let i = start; i < Math.min(cores, start + count); i++) next.add(i);
    onChange(formatAffinity(next, cores));
  };

  const cols = Math.min(16, cores);
  const allOn = selected.size === cores || value.trim() === '';
  return (
    <div className="space-y-2">
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, maxWidth: cols * 26 }}
        role="group"
        aria-label="CPU affinity"
      >
        {Array.from({ length: cores }, (_, i) => {
          const on = selected.has(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              disabled={disabled}
              className={`h-6 w-6 rounded text-[10px] font-mono tabular-nums cursor-pointer transition-colors border ${
                on
                  ? 'bg-accent/20 border-accent/60 text-accent'
                  : 'bg-bg-raised border-bg-border text-fg-dim hover:border-bg-ring hover:text-fg'
              } ${disabled ? '!cursor-not-allowed opacity-60' : ''}`}
              title={`Core ${i}`}
              aria-pressed={on}
            >
              {i}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-fg-dim">Quick:</span>
        <button type="button" onClick={() => setAll(true)} disabled={disabled} className="btn-quiet btn-sm !h-6 !text-[11px] !px-2">All</button>
        <button type="button" onClick={() => setAll(false)} disabled={disabled} className="btn-quiet btn-sm !h-6 !text-[11px] !px-2">None</button>
        {cores >= 4 && (
          <button type="button" onClick={() => setRange(1, cores - 1)} disabled={disabled} className="btn-quiet btn-sm !h-6 !text-[11px] !px-2">All but core 0</button>
        )}
        {cores >= 8 && (
          <button type="button" onClick={() => setRange(0, Math.floor(cores / 2))} disabled={disabled} className="btn-quiet btn-sm !h-6 !text-[11px] !px-2">First {Math.floor(cores / 2)}</button>
        )}
        {cores >= 8 && (
          <button type="button" onClick={() => setRange(Math.floor(cores / 2), Math.ceil(cores / 2))} disabled={disabled} className="btn-quiet btn-sm !h-6 !text-[11px] !px-2">Last {Math.ceil(cores / 2)}</button>
        )}
        <span className="ml-auto text-fg-dim">
          {allOn || selected.size === 0 ? (
            <span className="italic">{selected.size === 0 ? 'system default' : 'all cores'}</span>
          ) : (
            <>{selected.size} of {cores} · <code className="font-mono text-fg-muted">{value || '0'}</code></>
          )}
        </span>
      </div>
    </div>
  );
}

/** Parse Server_AFFINITY (any historical or new format) into a set of selected
 *  core indices. See file header for the format detection rules. */
function parseAffinity(raw: string, cores: number): Set<number> {
  const out = new Set<number>();
  const v = parseAffinityToBigInt(raw);
  if (v == null) return out;
  for (let i = 0; i < cores; i++) {
    if (((v >> BigInt(i)) & BigInt(1)) !== BigInt(0)) out.add(i);
  }
  return out;
}

export function parseAffinityToBigInt(raw: string | null | undefined): bigint | null {
  if (raw == null) return null;
  const s = raw.trim();
  if (!s) return null;
  try {
    if (/^0x[0-9a-fA-F]+$/.test(s)) return BigInt(s);
    if (/^[0-9]+$/.test(s))         return BigInt(s);              // decimal (v1)
    if (/^[0-9a-fA-F]+$/.test(s))   return BigInt('0x' + s);       // bare hex w/ letters
  } catch { /* fall through */ }
  return null;
}

/** Always emits "0x"-prefixed uppercase hex (or '' for system default). */
function formatAffinity(selected: Set<number>, cores: number): string {
  if (selected.size === 0) return '';
  if (selected.size === cores) return '';
  let v = BigInt(0);
  for (const idx of selected) v |= BigInt(1) << BigInt(idx);
  return '0x' + v.toString(16).toUpperCase();
}
